import { resetAllFieldStates, toPecFormApi } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import { formatISO } from 'date-fns'
import { SubmissionErrors } from 'final-form'
import { TipoFrequencia, TipoMotivoPrescricaoEnum, TurnoEnum, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-final-form'
import { MetaRoot } from 'util/metaPath'

import { AlertPrescricaoEnum } from '../components/AlertPrescricaoModal'
import { ConsultaHorusItemType } from '../hooks/usePrescricaoHorusIntegration'
import { MedicamentoFormModel, TipoDoseEnum } from '../model-prescricao'
import { createDefaultMedicamento } from '../utils/utils-prescricao'

export interface UsePrescricaoFormManagerProps {
  name: MetaRoot<MedicamentoFormModel>
  isEdit: boolean
  dataAtendimento: Date
  onClearEditedMedicamento: (medicamento: MedicamentoFormModel | null) => void
  onVerifyMedicamentoDuplicated: (medicamento: MedicamentoFormModel) => boolean
  setHorusData: (data: ConsultaHorusItemType[] | null) => void
  setActiveAlert: (alert: AlertPrescricaoEnum | null) => void
  onSubmit: (event?: React.SyntheticEvent<HTMLFormElement>) => Promise<SubmissionErrors | undefined> | undefined
  isRenovacao: boolean
  isReplicar: boolean
  medicamentoToEdit: MedicamentoFormModel | null
}

interface UsePrescricaoFormManagerResult {
  handleOnChangeTab: (tipoFrequencia: TipoFrequencia, tipoDose: TipoDoseEnum) => void
  handleOnReset: (registroManual: boolean) => void
  handleOnCheckMedicamentoNaoCadastrado: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOnChangeEscalaDuracao: () => void
  handleOnChangeTipoDose: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOnChangeUsoContinuo: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOnSubmitClick: (posologiaLabel: string, dataFimCalculada: Date | null | undefined) => void
}

export const usePrescricaoFormManager = (
  props: Readonly<UsePrescricaoFormManagerProps>
): UsePrescricaoFormManagerResult => {
  const {
    name,
    isEdit,
    dataAtendimento,
    onClearEditedMedicamento,
    onVerifyMedicamentoDuplicated,
    setHorusData,
    setActiveAlert,
    onSubmit,
    isRenovacao,
    isReplicar,
    medicamentoToEdit,
  } = props

  const form = toPecFormApi(useForm<MedicamentoFormModel>())
  const { mutators } = form
  const { MOTIVO_PRESCRICAO_ENABLED } = useFlags()

  const { input: tipoDoseInput } = usePecField({ name: name.tipoDose, subscription: { value: true } })
  const tipoDoseValue = tipoDoseInput.value

  const { input: tipoFrequenciaInput } = usePecField({ name: name.tipoFrequencia, subscription: { value: true } })
  const tipoFrequenciaValue = tipoFrequenciaInput.value

  const { input: idInput } = usePecField({ name: name.id, subscription: { value: true } })
  const idValue = idInput.value

  const {
    input: { onChange: onChangeTab },
  } = usePecField({ name: name.tipoFrequencia, subscription: { input: true } })

  useEffect(() => {
    const isRenovacaoOuReplicacao = isRenovacao || isReplicar

    if (isRenovacaoOuReplicacao && MOTIVO_PRESCRICAO_ENABLED && medicamentoToEdit) {
      const { tipoMotivoPrescricao, cidMotivoPrescricao } = medicamentoToEdit

      if (tipoMotivoPrescricao === TipoMotivoPrescricaoEnum.CID10 && cidMotivoPrescricao?.ativo === false) {
        setActiveAlert(AlertPrescricaoEnum.MOTIVO_INCOMPATIVEL)
        return
      }
    }
  }, [MOTIVO_PRESCRICAO_ENABLED, isRenovacao, isReplicar, medicamentoToEdit, setActiveAlert])

  const limparCamposPosologia = useCallback(() => {
    mutators.setValue(name.intervaloDose.alias, null)
    mutators.setValue(name.frequenciaDose.alias, null)
    mutators.setValue(name.qtDoseManha.alias, null)
    mutators.setValue(name.qtDoseTarde.alias, null)
    mutators.setValue(name.qtDoseNoite.alias, null)
  }, [mutators, name])

  const resetCamposDoseUnica = useCallback(() => {
    limparCamposPosologia()
    mutators.setValue(name.usoContinuo.alias, false)
    mutators.setValue(name.turno.alias, null)
    mutators.setValue(name.duracao.alias, 1)
    mutators.setValue(name.escalaDuracao.alias, UnidadeMedidaTempoEnum.DIAS)
    mutators.setValue(name.quantidadePeriodoFrequenciaTurno.alias, null)
    mutators.setValue(name.unidadeMedidaTempoFrequenciaTurno.alias, null)
  }, [limparCamposPosologia, mutators, name])

  const setValuesOnChangeTab = useCallback(
    (tipoFrequencia: TipoFrequencia) => {
      const isFrequenciaOuTurnoOuFracionado =
        tipoFrequencia === TipoFrequencia.FREQUENCIA ||
        tipoFrequencia === TipoFrequencia.TURNO ||
        tipoFrequencia === TipoFrequencia.FRACIONADO
      const isNotDoseUnica = tipoDoseValue !== TipoDoseEnum.UNICA
      const isFrequenciaOuTurnoOuFracionadoSemDoseUnica = isFrequenciaOuTurnoOuFracionado && isNotDoseUnica

      limparCamposPosologia()
      mutators.setValue(name.turno.alias, tipoFrequencia === TipoFrequencia.TURNO ? TurnoEnum.MANHA : null)
      mutators.setValue(
        name.quantidadePeriodoFrequenciaTurno.alias,
        isFrequenciaOuTurnoOuFracionadoSemDoseUnica ? 1 : null
      )
      mutators.setValue(
        name.unidadeMedidaTempoFrequenciaTurno.alias,
        isFrequenciaOuTurnoOuFracionadoSemDoseUnica ? UnidadeMedidaTempoEnum.DIAS : null
      )
    },
    [tipoDoseValue, limparCamposPosologia, mutators, name]
  )

  const setPadroesDoseComum = useCallback(() => {
    if (tipoDoseValue === TipoDoseEnum.UNICA) {
      mutators.setValue(name.duracao.alias, null)
    }
    if (tipoFrequenciaValue !== TipoFrequencia.INTERVALO) {
      mutators.setValue(name.quantidadePeriodoFrequenciaTurno.alias, 1)
      mutators.setValue(name.unidadeMedidaTempoFrequenciaTurno.alias, UnidadeMedidaTempoEnum.DIAS)
    }
    if (tipoFrequenciaValue === TipoFrequencia.TURNO) {
      mutators.setValue(name.turno.alias, TurnoEnum.MANHA)
    }
  }, [mutators, name, tipoDoseValue, tipoFrequenciaValue])

  const handleOnChangeTab = useCallback(
    (tipoFrequencia: TipoFrequencia, tipoDose: TipoDoseEnum) => {
      if (tipoDose === TipoDoseEnum.COMUM && tipoFrequencia !== TipoFrequencia.FRACIONADO) {
        onChangeTab(tipoFrequencia)
        setValuesOnChangeTab(tipoFrequencia)
      }
    },
    [onChangeTab, setValuesOnChangeTab]
  )

  const handleOnReset = useCallback(
    (registroManual: boolean) => {
      if (isEdit) {
        const id = idValue
        onClearEditedMedicamento(
          createDefaultMedicamento(dataAtendimento, {
            overrides: { id, registroManual },
            isMotivoPrescricaoEnabled: MOTIVO_PRESCRICAO_ENABLED,
          })
        )
        form.reset()
        resetAllFieldStates(form)
      } else {
        form.reset()
        resetAllFieldStates(form)
        mutators.setValue(name.registroManual.alias, registroManual)
      }
      setHorusData(null)
    },
    [
      isEdit,
      setHorusData,
      idValue,
      onClearEditedMedicamento,
      dataAtendimento,
      MOTIVO_PRESCRICAO_ENABLED,
      form,
      mutators,
      name,
    ]
  )

  const handleOnCheckMedicamentoNaoCadastrado = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked: boolean = e.target.checked
      handleOnReset(checked)
      setHorusData(null)
    },
    [handleOnReset, setHorusData]
  )

  const handleOnChangeEscalaDuracao = useCallback(() => {
    mutators.setValue(name.duracao.alias, null)
  }, [mutators, name.duracao.alias])

  const handleOnChangeTipoDose = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const tipoDoseSelecionado = e.target.value as TipoDoseEnum

      switch (tipoDoseSelecionado) {
        case TipoDoseEnum.UNICA:
          resetCamposDoseUnica()
          break
        case TipoDoseEnum.COMUM:
          setPadroesDoseComum()
          onChangeTab(TipoFrequencia.INTERVALO)
          setValuesOnChangeTab(TipoFrequencia.INTERVALO)
          break
        case TipoDoseEnum.FRACIONADA:
          setPadroesDoseComum()
          onChangeTab(TipoFrequencia.FRACIONADO)
          mutators.setValue(name.qtDose.alias, null)
          setValuesOnChangeTab(TipoFrequencia.FRACIONADO)
          break
      }
    },
    [resetCamposDoseUnica, setPadroesDoseComum, onChangeTab, mutators, setValuesOnChangeTab, name]
  )

  const handleOnChangeUsoContinuo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) return

      const isDoseUnica = tipoDoseValue === TipoDoseEnum.UNICA
      const isTurno = tipoFrequenciaValue === TipoFrequencia.TURNO
      const isNotIntervalo = tipoFrequenciaValue !== TipoFrequencia.INTERVALO

      if (isDoseUnica) {
        if (isTurno) mutators.setValue(name.turno.alias, TurnoEnum.MANHA)
        if (isNotIntervalo) {
          mutators.setValue(name.quantidadePeriodoFrequenciaTurno.alias, 1)
          mutators.setValue(name.unidadeMedidaTempoFrequenciaTurno.alias, UnidadeMedidaTempoEnum.DIAS)
        }
        mutators.setValue(name.tipoDose.alias, TipoDoseEnum.COMUM)
        mutators.setValue(name.tipoFrequencia.alias, TipoFrequencia.INTERVALO)
      }
      mutators.setValue(name.duracao.alias, null)
      mutators.setValue(name.escalaDuracao.alias, UnidadeMedidaTempoEnum.INDETERMINADO)
    },
    [mutators, name, tipoDoseValue, tipoFrequenciaValue]
  )

  const triggerSubmit = useCallback(() => {
    onSubmit()
    setActiveAlert(AlertPrescricaoEnum.SALVO)
  }, [onSubmit, setActiveAlert])

  const handleOnSubmitClick = useCallback(
    (posologiaLabel: string, dataFimCalculada: Date | null | undefined) => {
      const dataFimFormatada = dataFimCalculada ? formatISO(dataFimCalculada, { representation: 'date' }) : null
      const currentValues = form.getState().values
      const finalValues = { ...currentValues, posologia: posologiaLabel, dataFimTratamento: dataFimFormatada }
      const applyChangesAndSubmit = () => {
        if (currentValues.tipoMotivoPrescricao === TipoMotivoPrescricaoEnum.CID10) {
          form.change(name.ciapMotivoPrescricao.alias, null)
        } else if (currentValues.tipoMotivoPrescricao === TipoMotivoPrescricaoEnum.CIAP2) {
          form.change(name.cidMotivoPrescricao.alias, null)
        }

        form.change(name.posologia.alias, posologiaLabel)
        form.change(name.dataFimTratamento.alias, dataFimFormatada)

        setTimeout(triggerSubmit)
      }

      if (onVerifyMedicamentoDuplicated(finalValues)) {
        confirm({
          title: 'Deseja salvar este medicamento?',
          body: 'Este medicamento já foi inserido na prescrição do atendimento.',
          type: 'primary',
          onConfirm: () => {
            applyChangesAndSubmit()
          },
          confirmLabel: 'Salvar',
          depthLevel: 2,
          manageOverflow: false,
        })()
      } else {
        applyChangesAndSubmit()
      }
    },
    [onVerifyMedicamentoDuplicated, triggerSubmit, form, name]
  )

  return {
    handleOnChangeTab,
    handleOnReset,
    handleOnCheckMedicamentoNaoCadastrado,
    handleOnChangeEscalaDuracao,
    handleOnChangeTipoDose,
    handleOnChangeUsoContinuo,
    handleOnSubmitClick,
  }
}
